/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router';
import PaymentPage from '../containers/PaymentPage';
import ErrorPage from '../containers/ErrorPage';
import LogCannon from '@ace-clublabs/log-cannon';
import { useGlobalState } from '../shared/state';
import { ErrorType } from '../shared/types';
import { RedirectPage } from '../containers/RedirectPage';
import AutoPayTermsAndConditions from '../containers/AutoPayTermsAndConditions';
import { PaymentDate } from '../containers/PaymentDate';
import { Pages } from '../shared/pages';
import { PaymentMethods } from '../containers/PaymentMethods';
import { ConfirmationPage } from '../containers/ConfirmationPage';
import PaymentRouter from '../containers/PaymentRouter';
import { PayByLink } from '../containers/PayByLink';
import { PayByLinkAuthentication } from '../containers/PayByLinkAuthentication';
import { MANIFEST_TTL_MS } from '../constants';

const MANIFEST_REQUIRED_PATHS = [Pages.PaymentMethods, Pages.PaymentPage, Pages.SchedulePayment];
import EmbedTestParent from '../containers/EmbedTestParent';

const Router: React.FC = () => {
  let location = useLocation();
  const navigate = useNavigate();
  const isEmbedTestParentEnabled =
    import.meta.env.VITE_ENV !== 'prod' || window.location.hostname !== 'app.ace.aaa.com';

  useEffect(() => {
    LogCannon.blast(`Navigate to ${location.pathname}`, {
      pathname: location.pathname,
      search: location.search,
    });
  }, [location]);

  const [manifest, setManifest] = useGlobalState('manifest');
  const [error, setError] = useGlobalState('error');
  const [manifestId] = useGlobalState('manifestId');
  const [manifestTimestamp] = useGlobalState('manifestTimestamp');

  const state = {
    customerId: useGlobalState('customerId')[0],
    epcRequestId: useGlobalState('epcRequestId')[0],
    error,
    isMobileApp: useGlobalState('isMobileApp')[0],
    isScheduled: useGlobalState('isScheduled')[0],
    manifest,
    manifestId,
    manifestTimestamp,
    payByLinkData: useGlobalState('payByLinkData')[0],
    paymentMethodsSkipped: useGlobalState('paymentMethodsSkipped')[0],
    paymentProcessing: useGlobalState('paymentProcessing')[0],
    paymentType: useGlobalState('paymentType')[0],
    redirectMap: useGlobalState('redirectMap')[0],
    renderAuthorization: useGlobalState('renderAuthorization')[0],
    scheduledDate: useGlobalState('scheduledDate')[0],
    sessionId: useGlobalState('sessionId')[0],
  };

  useEffect(() => {
    sessionStorage.setItem('paymentsState', JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    const isMissingManifestPath = location.pathname === '/';
    const isManifestStale = !!manifestTimestamp && Date.now() - manifestTimestamp > MANIFEST_TTL_MS;
    const isManifestRequiredPath = MANIFEST_REQUIRED_PATHS.includes(location.pathname as Pages);
    if (isMissingManifestPath || (isManifestStale && isManifestRequiredPath)) {
      setManifest(undefined);
      setError({ errorMessage: 'Session expired', errorType: ErrorType.SESSION_TIMEOUT });
      LogCannon.blast('Navigating to Session Error', { manifestId, pathname: location.pathname });
      navigate(Pages.Error);
    }
  }, [location]);

  return (
    <Routes>
      <Route path={Pages.PaymentPage} element={<PaymentPage />} />
      <Route path={Pages.SchedulePayment} element={<PaymentDate />} />
      <Route path={Pages.PaymentMethods} element={<PaymentMethods />} />
      <Route path={Pages.ConfirmationPage} element={<ConfirmationPage />} />
      <Route path={Pages.PayByLinkAuthentication} element={<PayByLinkAuthentication />} />
      <Route path={Pages.PayByLink} element={<PayByLink />} />
      {isEmbedTestParentEnabled && <Route path={Pages.EmbedTestParent} element={<EmbedTestParent />} />}
      <Route path="/redirect/:id" element={<RedirectPage />} />
      <Route path="/:id" element={<PaymentRouter />} />
      <Route path="/termsandconditions" element={<AutoPayTermsAndConditions />} />
      <Route path={Pages.Error} element={<ErrorPage />} />
      <Route path="*" element={<ErrorPage />} />
    </Routes>
  );
};

export default Router;
